import { useState } from 'react';
import { router } from 'expo-router';
import { useVideoProvider } from '@/shared/hooks/useVideoProvider';
import SentryService from '@/services/sentry/Sentry.service';
import { ROUTES } from '@/shared/constants/routes';
import useStore from '@/stores/useStore';
import { Consultation } from '@/shared/types/generated';
import { GET_CONSULTATION } from '../queries';
import { useLazyQuery } from '@apollo/client';

interface UseJoinCallProps {
  consultation: Consultation;
}

interface UseJoinCallReturn {
  handleJoinCall: () => Promise<void>;
  isJoining: boolean;
}

export const useJoinCall = ({
  consultation,
}: UseJoinCallProps): UseJoinCallReturn => {
  const [isJoining, setIsJoining] = useState(false);
  const { isStylist, setSnackbarState } = useStore();
  const { startSession, joinSession } = useVideoProvider();

  const [getConsultation] = useLazyQuery(GET_CONSULTATION, {
    variables: {
      input: {
        id: consultation.id,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const handleJoinCall = async () => {
    setIsJoining(true);

    if (isStylist) {
      try {
        // Stylist starts session with client's user ID
        const clientUserId = consultation.client?.user?.id;
        if (!clientUserId) {
          throw new Error('Client user ID not found');
        }
        await startSession(clientUserId, consultation.id);
        router.push(ROUTES.VIDEO_SESSION.path);
      } catch (error) {
        setIsJoining(false);
        SentryService.captureError(
          'Failed to start video session from sessions page',
          error,
        );
      }
    } else {
      // For clients, try to join existing session
      const sessionName = consultation.videoSession?.sessionName;

      if (sessionName) {
        try {
          await joinSession(sessionName);
          setIsJoining(false);
          router.push(ROUTES.VIDEO_SESSION.path);
        } catch (error) {
          setIsJoining(false);
          SentryService.captureError(
            'Failed to join in progress video session',
            error,
          );
        }
      } else {
        try {
          const response = await getConsultation();
          if (response.data?.consultation?.videoSession?.sessionName) {
            await joinSession(
              response.data.consultation.videoSession.sessionName,
            );
            router.push(ROUTES.VIDEO_SESSION.path);
            return;
          }
          setSnackbarState({
            open: true,
            message:
              "Your stylist hasn't joined the call yet. Please try again in a minute.",
            severity: 'info',
          });
        } catch (error) {
          SentryService.captureError(
            'Failed to get consultation when joining call',
            error,
          );
          setSnackbarState({
            open: true,
            message:
              'Failed to join the call. Please try again. If this issue persists, close and re-open the app.',
            severity: 'error',
          });
        } finally {
          setIsJoining(false);
        }
      }
    }
  };

  return {
    handleJoinCall,
    isJoining,
  };
};
